import { Vector3 } from 'three';
import { FiniteStateMachine, type FSMConfig } from './fsm';
import type { NavMeshQuery } from './navmesh';

export type EnemyState = 'patrol' | 'investigate' | 'attack';
export type EnemyEvent = 'spotted' | 'noise' | 'lost-sight' | 'search-timeout';

export interface EnemyAIConfig {
  navMesh: NavMeshQuery;
  /** Cyclic waypoints walked in order while patrolling. */
  patrolRoute: Vector3[];
  /** Movement speed in world units/second, used for both patrol and investigate/attack pursuit. */
  moveSpeed?: number;
  /** Distance at which a waypoint/target counts as "reached". */
  arrivalRadius?: number;
  /** How long to stand at a noise's source before giving up and resuming patrol. */
  investigateTimeoutSeconds?: number;
  /** Optional telemetry callback for state changes */
  onStateChange?: (newState: EnemyState, previousState: EnemyState, event: EnemyEvent | null) => void;
}

const DEFAULTS = {
  moveSpeed: 3,
  arrivalRadius: 0.25,
  investigateTimeoutSeconds: 5,
};

function buildTransitions(): FSMConfig<EnemyState, EnemyEvent>['transitions'] {
  return {
    patrol: { spotted: 'attack', noise: 'investigate' },
    investigate: { spotted: 'attack', 'search-timeout': 'patrol' },
    attack: { 'lost-sight': 'investigate' },
  };
}

/**
 * Drives one enemy's patrol -> investigate -> attack behavior. Stimuli (`reportSighting`,
 * `reportNoise`, `reportLostSight`) come from gameplay/perception code; `update(dt)` advances
 * movement along the navmesh and raises the internal `search-timeout` event once an
 * investigation has run its course without a fresh sighting.
 */
export class EnemyAIController {
  readonly position: Vector3;
  private readonly fsm: FiniteStateMachine<EnemyState, EnemyEvent>;
  private readonly navMesh: NavMeshQuery;
  private readonly patrolRoute: Vector3[];
  private readonly moveSpeed: number;
  private readonly arrivalRadius: number;
  private readonly investigateTimeoutSeconds: number;

  private patrolIndex = 0;
  private investigateOrigin: Vector3 | null = null;
  private investigateElapsed = 0;
  private attackTarget: Vector3 | null = null;
  private currentPath: Vector3[] = [];
  private repathTimer = 0;

  constructor(startPosition: Vector3, config: EnemyAIConfig) {
    if (config.patrolRoute.length === 0) {
      throw new Error('EnemyAIController requires at least one patrol waypoint');
    }
    this.position = startPosition.clone();
    this.navMesh = config.navMesh;
    this.patrolRoute = config.patrolRoute;
    this.moveSpeed = config.moveSpeed ?? DEFAULTS.moveSpeed;
    this.arrivalRadius = config.arrivalRadius ?? DEFAULTS.arrivalRadius;
    this.investigateTimeoutSeconds = config.investigateTimeoutSeconds ?? DEFAULTS.investigateTimeoutSeconds;

    this.fsm = new FiniteStateMachine<EnemyState, EnemyEvent>({
      initial: 'patrol',
      transitions: buildTransitions(),
      onEnter: {
        patrol: (event, prev) => {
          if (prev) config.onStateChange?.('patrol', prev, event);
        },
        investigate: (event, prev) => {
          this.investigateElapsed = 0;
          this.currentPath = [];
          if (prev) config.onStateChange?.('investigate', prev, event);
        },
        attack: (event, prev) => {
          this.repathTimer = 0;
          if (prev) config.onStateChange?.('attack', prev, event);
        },
      },
    });
  }

  get state(): EnemyState {
    return this.fsm.state;
  }

  get lastKnownPosition(): Vector3 | null {
    return this.investigateOrigin;
  }

  /** Perception reports the player is visible; always wins and switches to attack. */
  reportSighting(playerPosition: Vector3): void {
    this.attackTarget = playerPosition.clone();
    this.fsm.dispatch('spotted');
  }

  /** Perception reports a noise; only patrol reacts (an already-alerted enemy ignores it). */
  reportNoise(noisePosition: Vector3): void {
    if (this.fsm.dispatch('noise')) {
      this.investigateOrigin = noisePosition.clone();
      this.currentPath = [];
    }
  }

  /** Perception reports the player is no longer visible; attack falls back to investigating the last known position. */
  reportLostSight(): void {
    if (this.attackTarget && this.fsm.dispatch('lost-sight')) {
      this.investigateOrigin = this.attackTarget.clone();
      this.attackTarget = null;
      this.currentPath = [];
    }
  }

  /** Advances movement/timers for the current state by `dt` seconds. */
  update(dt: number): void {
    switch (this.fsm.state) {
      case 'patrol':
        this.updatePatrol(dt);
        break;
      case 'investigate':
        this.updateInvestigate(dt);
        break;
      case 'attack':
        this.updateAttack(dt);
        break;
    }
  }

  private updatePatrol(dt: number): void {
    const target = this.patrolRoute[this.patrolIndex];
    if (this.moveToward(target, dt)) {
      this.patrolIndex = (this.patrolIndex + 1) % this.patrolRoute.length;
      this.currentPath = [];
    }
  }

  private updateInvestigate(dt: number): void {
    if (!this.investigateOrigin) return;

    this.investigateElapsed += dt;
    this.moveToward(this.investigateOrigin, dt);

    if (this.investigateElapsed >= this.investigateTimeoutSeconds) {
      this.fsm.dispatch('search-timeout');
      this.investigateOrigin = null;
      this.investigateElapsed = 0;
      this.currentPath = [];
    }
  }

  private updateAttack(dt: number): void {
    if (!this.attackTarget) return;

    if (this.position.distanceTo(this.attackTarget) <= this.arrivalRadius) {
      this.currentPath = [];
      return;
    }

    this.repathTimer -= dt;
    if (this.currentPath.length === 0 || this.repathTimer <= 0) {
      this.repathTimer = 0.35;
      const path = this.navMesh.findPath(this.position, this.attackTarget);
      if (path && path.length > 0) {
        this.currentPath = path;
      } else if (this.currentPath.length === 0) {
        this.currentPath = [this.attackTarget.clone()];
      }
    }

    this.stepAlongPath(dt);
  }

  /** Moves along a navmesh path toward `target`; returns true once within arrival radius. */
  private moveToward(target: Vector3, dt: number): boolean {
    if (this.position.distanceTo(target) <= this.arrivalRadius) {
      this.currentPath = [];
      return true;
    }

    if (this.currentPath.length === 0) {
      this.currentPath = this.navMesh.findPath(this.position, target) ?? [target.clone()];
    }

    return this.stepAlongPath(dt);
  }

  private stepAlongPath(dt: number): boolean {
    if (this.currentPath.length === 0) return true;

    const waypoint = this.currentPath[0];
    const toWaypoint = waypoint.clone().sub(this.position);
    const distance = toWaypoint.length();

    const step = this.moveSpeed * dt;
    if (distance <= step || distance <= this.arrivalRadius) {
      this.position.copy(waypoint);
      this.currentPath.shift();
      return this.currentPath.length === 0;
    } else {
      this.position.add(toWaypoint.multiplyScalar(step / distance));
      return false;
    }
  }
}
